import { useEffect, useCallback } from 'react';
import { useCookieConsent } from './useCookieConsent';
import { GA4_MEASUREMENT_ID } from '@/config/googleAnalytics';

// GA4 Measurement ID from config
const GA_ID = GA4_MEASUREMENT_ID;

/**
 * Hook to conditionally load analytics/marketing scripts based on user consent.
 * Scripts are only loaded after explicit user consent.
 */
export const useConsentScripts = () => {
  const { consentData, isAllowed } = useCookieConsent();

  const loadScript = useCallback((src: string, id: string, async = true): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.id = id;
      script.async = async;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }, []);

  const loadInlineScript = useCallback((content: string, id: string) => {
    if (document.getElementById(id)) {
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.innerHTML = content;
    document.head.appendChild(script);
  }, []);

  // Load Google Analytics 4 when analytics consent is given
  useEffect(() => {
    console.log('[ConsentScripts] Effect triggered:', {
      consentData: consentData ? 'exists' : 'null',
      analyticsAllowed: consentData ? isAllowed('analytics') : 'no consent yet',
      GA_ID: GA_ID || 'NOT SET'
    });
    
    if (!consentData) {
      console.log('[ConsentScripts] Waiting for consent data...');
      return;
    }

    if (isAllowed('analytics') && GA_ID) {
      console.log('[ConsentScripts] Loading GA4 script...');
      // Load GA4 gtag.js
      loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, 'ga4-script')
        .then(() => {
          console.log('[GA4] Script loaded, initializing...');
          // Initialize gtag after script loads
          loadInlineScript(`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GA_ID}', {
              send_page_view: true,
              cookie_flags: 'SameSite=None;Secure'
            });
            console.log('[GA4] gtag configured with ID: ${GA_ID}');
          `, 'ga4-config');
          
          console.log('[GA4] Google Analytics 4 initialized with ID:', GA_ID);
        })
        .catch((error) => {
          console.error('[GA4] Failed to load Google Analytics:', error);
        });
    } else {
      console.log('[ConsentScripts] GA4 not loading:', {
        analyticsAllowed: isAllowed('analytics'),
        GA_ID_exists: !!GA_ID
      });
    }

    if (isAllowed('marketing')) {
      // Meta Pixel is handled by useMetaPixel hook
      console.log('[Marketing] Marketing consent granted');
    }
  }, [consentData, isAllowed, loadScript, loadInlineScript]);

  return {
    analyticsAllowed: isAllowed('analytics'),
    marketingAllowed: isAllowed('marketing'),
    loadScript,
    loadInlineScript,
    ga4Enabled: isAllowed('analytics') && !!GA_ID,
  };
};
